import { Chip } from '@mui/material';
import { styled } from '@mui/material/styles';
import { beautifyEnum } from 'src/utils/utility';
import { CustomTooltip } from 'src/presentations/components/atom/customtooltip';
import IconInfo from 'src/assets/images/icons/info-circle.svg';
import IconInfoGray from 'src/assets/images/icons/info-circle-grey-2.svg';
import { processingStatuses } from './configs';
import StatusChip from '../../organisms/dashboard/StatusChip';

export interface InvoiceData {
  status?: string;
  invoiceStatus?: string;
  finalResolution?: string;
}

interface StatusRenderOptions {
  size?: 'small' | 'medium';
  className?: string;
  showDot?: boolean;
}

// Styled components
const StyledChip = styled(Chip)({
  background: '#ffffff',
  border: '1px solid #D9D9D9',
  borderRadius: '20px',
  color: '#A3A3A3',
  height: '32px',
});

const StyledPartialChip = styled(Chip)({
  background: 'rgba(255, 193, 7, 0.12)',
  border: '1px solid #FFC107',
  borderRadius: '20px',
  color: '#FFC107',
  height: '32px',
});

/**
 * Renders the status chip for an invoice
 * Exported for reuse in other components
 */
export const renderInvoiceStatusChip = (invoiceData: InvoiceData, size: 'small' | 'medium' = 'small', className?: string) => (
  <StatusChip status={invoiceData?.status} size={size} className={className} />
);

/**
 * Renders additional status messages (like "No Rate Card Found" or rejection reasons)
 * Exported for reuse in other components
 */
export const renderInvoiceStatusMessages = (invoiceData: InvoiceData, errorProcessingStatuses: string[], options: { showDot?: boolean } = {}) => {
  const { showDot = false } = options;
  const isProcessing = processingStatuses.includes(invoiceData?.status || '');
  const isErrorProcessing = errorProcessingStatuses.includes(invoiceData?.status || '');

  if (isProcessing) {
    return null;
  }

  // Removed "No Rate Card Found" message completely

  if (invoiceData?.invoiceStatus === 'REJECTED' && !isErrorProcessing) {
    if (showDot) {
      return (
        <>
          <div key='rejected' className='w-4 h-4 rounded-full bg-[#D9D9D9]' />
          <div key='rejected-text' className='text-neutral-60 text-12'>
            {beautifyEnum(invoiceData?.finalResolution)}
          </div>
        </>
      );
    }
    return <p className='text-12 text-red-40'>{beautifyEnum(invoiceData?.finalResolution)}</p>;
  }
  return null;
};

/**
 * Renders the appropriate status component based on priority rules
 * Exported for reuse in other components
 */
export const renderInvoiceStatusComponent = (invoiceData: InvoiceData, errorProcessingStatuses: string[], options: StatusRenderOptions = {}) => {
  const { size = 'small', className = '' } = options;

  if (invoiceData?.status === 'DUPLICATE') {
    return <StatusChip status={invoiceData?.status} size={size} className={className} />;
  }

  // Partial status
  if (invoiceData?.invoiceStatus === 'PARTIAL') {
    return (
      <StyledPartialChip
        label={
          <div className='flex items-center px-4 gap-8 font-medium'>
            <span>Partially Processed</span>
            <CustomTooltip title='Partially Processed'>
              <img width={20} src={IconInfoGray} alt='' />
            </CustomTooltip>
          </div>
        }
        size={size}
      />
    );
  }

  if (invoiceData?.invoiceStatus) {
    return <StatusChip status={invoiceData?.invoiceStatus} size={size} className={className} />;
  }

  const isProcessing = processingStatuses.includes(invoiceData?.status || '');
  if (isProcessing) {
    return <StatusChip status={invoiceData?.invoiceStatus} size={size} className={`flex items-center px-4 gap-8 ${className}`} />;
  }

  // Check for error processing statuses that should show Under Review
  const isErrorProcessing = errorProcessingStatuses.includes(invoiceData?.status || '');
  if (isErrorProcessing) {
    return (
      <StyledChip
        label={
          <div className='flex items-center px-4 gap-8'>
            <span className='font-medium'>Under Review</span>
            <CustomTooltip title='Under investigation by the Support Team who will get in touch with you shortly'>
              <img width={20} src={IconInfo} alt='' />
            </CustomTooltip>
          </div>
        }
        style={{
          background: 'rgba(89, 114, 130, 0.12)',
          border: '1px solid #597282',
          borderRadius: '20px',
          color: '#597282',
        }}
        size={size}
      />
    );
  }

  return <StatusChip status={invoiceData?.status} size={size} className={className} />;
};
